import React, {useState, useEffect} from 'react';
import moment from 'moment';
import scheduleUtils from '../scheduleUtils';
import DatePickerComponentRender from './datePickerComponentRender';

const DatePickerComponent = (props) => {
  const {
    selected,
    alreadyBookedTimeRanges,
    timeIntervals,
    sessionTimeLimit,
    onMonthChange,
    maxDate,
    scheduleSettingsDto,
    selectedTimezone = scheduleUtils.generateGuessedTimezone(),
    isBoundedMonths,
    timeRangeStartDate,
    isEndDateCalender,
    widgetLanguageLocaleKey,
    minDate,
  } = props;
  const [selectedMonthYear, setSelectedMonthYear] = useState(
    moment(selected).format('MMM YYYY'),
  );
  const [selectedMonth, setSelectedMonth] = useState(
    scheduleUtils
      .getLocaleSpecificMomentObj(selected, widgetLanguageLocaleKey)
      .format('MMMM'),
  );
  const [dayNames, setDayNames] = useState([]);
  const selectedDayInMonth =
    moment(selected).format('MM') ===
    moment(selectedMonthYear, ['MMM YYYY']).format('MM')
      ? moment(selected).tz(selectedTimezone).date()
      : -1;

  let daysArray = [];
  if (props.availableSlotsListsByDate) {
    {
      daysArray = Object.keys(props.availableSlotsListsByDate).filter(
        (day) => day !== ' ',
      );
      daysArray = daysArray.map((day) => parseInt(day));
    }

    const emptySlots = props.availableSlotsListsByDate[' '] || [];
    const numberOfEmptySlots = Array.isArray(emptySlots)
      ? emptySlots.length
      : emptySlots;

    // Create array of empty slots (negative numbers or empty strings)
    const emptyDaysArray = Array(numberOfEmptySlots).fill('');

    // Prepend empty slots to the days array
    daysArray = [...emptyDaysArray, ...daysArray];
  } else {
    daysArray = scheduleUtils.generateDaysArray(
      moment(selectedMonthYear, ['MMM YYYY']).format('YYYY'),
      moment(selectedMonthYear, ['MMM YYYY']).format('MM'),
      props.weekStartDay,
    );
  }

  useEffect(() => {
    if (
      (selected && selectedMonthYear !== moment(selected).format('MMM YYYY')) ||
      widgetLanguageLocaleKey
    ) {
      setSelectedMonthYear(moment(selected).format('MMM YYYY'));
      setSelectedMonth(
        scheduleUtils
          .getLocaleSpecificMomentObj(selected, widgetLanguageLocaleKey)
          .format('MMMM'),
      );
    }
  }, [selected, widgetLanguageLocaleKey]);

  useEffect(() => {
    getDayNames();
  }, [widgetLanguageLocaleKey]);

  const addMonth = (e) => {
    e.stopPropagation();
    if (isBoundedMonths) {
      const date = new Date(
        moment(selectedMonthYear, ['MMM YYYY']).add(1, 'month'),
      );
      if (moment(date).isAfter(moment(new Date()))) return;
    }
    const newMonth = moment(selectedMonthYear, ['MMM YYYY'])
      .add(1, 'month')
      .format('MMM YYYY');
    setSelectedMonthYear(newMonth);
    setSelectedMonth(
      scheduleUtils
        .getLocaleSpecificMomentObj(
          selectedMonthYear,
          widgetLanguageLocaleKey,
          ['MMM YYYY'],
        )
        .add(1, 'month')
        .format('MMMM'),
    );
    if (onMonthChange) {
      onMonthChange(newMonth);
    }
  };
  const subMonth = (e) => {
    e.stopPropagation();
    if (isEndDateCalender) {
      const rangeStartMonth = new Date(
        moment(timeRangeStartDate).startOf('month'),
      );
      const date = new Date(
        moment(selectedMonthYear, ['MMM YYYY']).add(-1, 'month'),
      );
      if (moment(date).isBefore(rangeStartMonth)) return;
    }
    const newMonth = moment(selectedMonthYear, ['MMM YYYY'])
      .add(-1, 'month')
      .format('MMM YYYY');
    setSelectedMonthYear(newMonth);
    setSelectedMonth(
      scheduleUtils
        .getLocaleSpecificMomentObj(
          selectedMonthYear,
          widgetLanguageLocaleKey,
          ['MMM YYYY'],
        )
        .add(-1, 'month')
        .format('MMMM'),
    );
    if (onMonthChange) {
      onMonthChange(newMonth);
    }
  };

  const isCurrentDay = (day) => {
    if (day < 1) return false;
    const date = moment(selectedMonthYear, ['MMM YYYY'])
      .startOf('month')
      .add({
        day: day - 1,
      });
    return moment().format('YYYY-MM-DD') === moment(date).format('YYYY-MM-DD');
  };

  const isValidDay = (day) => {
    if (isBoundedMonths) {
      const date = new Date(
        moment(selectedMonthYear, ['MMM YYYY'])
          .startOf('month')
          .add(day, 'day'),
      );
      return moment(date).isBefore(moment().add(1, 'day'));
    }
    let date = moment(selectedMonthYear, ['MMM YYYY'])
      .startOf('month')
      .add({
        day: day - 1,
      });
    const currentTimeInSelectedZone = moment(date).tz(selectedTimezone);
    if (date.day() !== currentTimeInSelectedZone.day()) {
      date = moment(selectedMonthYear, ['MMM YYYY']).startOf('month').add({
        day: day,
      });
    }

    let alreadyBookedTimeRangesByday = [];

    if (!props.isMentor && alreadyBookedTimeRanges) {
      if (typeof alreadyBookedTimeRanges === 'object') {
        alreadyBookedTimeRangesByday =
          Object.values(alreadyBookedTimeRanges)[0]?.get(day) || [];
      } else {
        alreadyBookedTimeRangesByday = alreadyBookedTimeRanges.get(day);
      }
    }

    return scheduleUtils.isValidDay(
      date,
      alreadyBookedTimeRangesByday,
      scheduleSettingsDto,
      maxDate,
      timeIntervals,
      sessionTimeLimit,
      selectedTimezone,
      props.isMentor,
      minDate,
    );
  };

  const setDate = (date) => {
    if (checkIfDayIsValid(date)) {
      const newDate = new Date(
        moment(selectedMonthYear, ['MMM YYYY'])
          .startOf('month')
          .add({
            day: date - 1,
          }),
      );
      props.onChange(newDate);
    }
  };

  const getDayNames = async () => {
    const dayNames = await scheduleUtils.getLocaleSpecificWeekDays(
      widgetLanguageLocaleKey,
      props.weekStartDay,
    );
    setDayNames(dayNames);
  };
  const checkIfDayIsValid = props.availableSlotsListsByDate
    ? (day) => {
        return (
          props.availableSlotsListsByDate?.[day] &&
          props.availableSlotsListsByDate[day].some(
            (time) => time.availableMentorIds?.length > 0,
          )
        );
      }
    : isValidDay;

  return (
    <DatePickerComponentRender
      isBoundedMonths={isBoundedMonths}
      subMonth={subMonth}
      selectedMonthYear={selectedMonthYear}
      selectedMonth={selectedMonth}
      addMonth={addMonth}
      dayNames={dayNames}
      daysArray={daysArray}
      isLoading={props.isLoading}
      selectedDayInMonth={selectedDayInMonth}
      setDate={setDate}
      isCurrentDay={isCurrentDay}
      checkIfDayIsValid={checkIfDayIsValid}
    />
  );
};

export default DatePickerComponent;
